<script lang="ts">
	import LoaderCircleIcon from '@lucide/svelte/icons/loader-circle';
	import SendHorizontalIcon from '@lucide/svelte/icons/send-horizontal';
	import TriangleAlertIcon from '@lucide/svelte/icons/triangle-alert';
	import CheckIcon from '@lucide/svelte/icons/check';

	type Props = {
		toolsCount: number;
		skillsCount: number;
		installing: boolean;
		done: boolean;
		error: string | null;
		onFinish: () => void;
		onBack: () => void;
		onStarterPrompt: (text: string) => void;
		onContinue: () => void;
	};
	let {
		toolsCount,
		skillsCount,
		installing,
		done,
		error,
		onFinish,
		onBack,
		onStarterPrompt,
		onContinue,
	}: Props = $props();

	// Hardcoded starter prompts — generic enough to be useful with zero picks
	// (the "zero is fine" path of the wizard). A server-suggested set is tracked
	// as roadmap follow-up F2.
	const STARTER_PROMPTS = [
		'What can you help me with today?',
		'Show me an example of how to use my tools and skills together.',
		'Walk me through what to try first.',
	];
</script>

<div class="flex flex-col items-center gap-7 pt-2 text-center">
	<!-- ── Pre-install state ──────────────────────────────────────────────── -->
	{#if !done}
		<div class="flex flex-col items-center gap-3">
			<h2
				class="text-[28px] font-medium tracking-[-0.025em] text-[color:var(--fg-strong)]"
			>
				Ready.
			</h2>
			<p
				class="max-w-sm text-[13px] leading-relaxed text-[color:var(--muted-foreground)]"
			>
				You can change any of this later.
			</p>
		</div>

		<!-- Glass summary pill -->
		<div
			class="inline-flex items-center gap-3.5 rounded-full border px-5 py-3 text-[12.5px] text-[color:var(--muted-foreground)] backdrop-blur-md"
			style="background: var(--glass); border-color: var(--glass-border-strong);"
		>
			<span>
				<strong class="font-medium text-[color:var(--fg-strong)]">{toolsCount}</strong>
				{toolsCount === 1 ? 'tool' : 'tools'}
			</span>
			<span
				class="size-[3px] rounded-full bg-[color:var(--fg-faint)] opacity-60"
				aria-hidden="true"
			></span>
			<span>
				<strong class="font-medium text-[color:var(--fg-strong)]">{skillsCount}</strong>
				{skillsCount === 1 ? 'skill' : 'skills'}
			</span>
		</div>

		{#if error}
			<div
				class="flex w-full max-w-md items-start gap-2 rounded-[14px] border px-3 py-2.5 text-left text-[12.5px]"
				style="
					background: color-mix(in srgb, var(--destructive) 8%, var(--glass));
					border-color: color-mix(in srgb, var(--destructive) 30%, transparent);
					color: var(--destructive);
				"
			>
				<TriangleAlertIcon class="mt-0.5 size-4 shrink-0" />
				<div class="min-w-0 flex-1">
					<p class="font-medium">Something went wrong while installing.</p>
					<p class="mt-0.5 break-words opacity-90">{error}</p>
				</div>
			</div>
		{/if}

		<div class="flex items-center justify-center gap-2.5 pt-2">
			<button
				type="button"
				onclick={onBack}
				disabled={installing}
				class="inline-flex items-center justify-center rounded-full border px-5 py-2 text-[13px] font-medium text-[color:var(--muted-foreground)] backdrop-blur-md transition-colors hover:text-[color:var(--fg-strong)] disabled:opacity-60"
				style="background: var(--glass); border-color: var(--glass-border);"
			>
				Back
			</button>
			<button
				type="button"
				onclick={onFinish}
				disabled={installing}
				class="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-2.5 text-[13px] font-medium text-primary-foreground transition-colors hover:bg-[color:var(--primary)]/90 disabled:opacity-60"
				style="box-shadow: 0 8px 28px -10px var(--glow), 0 0 0 1px rgba(255,255,255,0.06) inset;"
			>
				{#if installing}
					<LoaderCircleIcon class="size-3.5 animate-spin" />
					Installing…
				{:else if error}
					Try again
				{:else}
					Start chatting
				{/if}
			</button>
		</div>
	{/if}

	<!-- ── Post-install state (success, chips, continue) ──────────────────── -->
	{#if done}
		<!-- Check medallion: glass tile + soft glow ring -->
		<div class="relative">
			<div
				class="grid size-14 place-items-center rounded-[14px] border bg-[var(--glass)] backdrop-blur-md"
				style="border-color: var(--glass-border-strong);"
			>
				<CheckIcon class="size-6 text-primary" strokeWidth={2.5} />
			</div>
			<div
				class="pointer-events-none absolute -inset-px rounded-[14px]"
				style="box-shadow: 0 0 30px var(--glow);"
				aria-hidden="true"
			></div>
		</div>

		<div class="flex flex-col items-center gap-2">
			<h2
				class="text-[26px] font-medium tracking-[-0.025em] text-[color:var(--fg-strong)]"
			>
				All set.
			</h2>
			<p
				class="max-w-sm text-[12.5px] leading-relaxed text-[color:var(--muted-foreground)]"
			>
				Try one of these prompts, or jump straight in.
			</p>
		</div>

		<div class="flex max-w-lg flex-wrap justify-center gap-2">
			{#each STARTER_PROMPTS as prompt (prompt)}
				<button
					type="button"
					onclick={() => onStarterPrompt(prompt)}
					class="inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[12px] font-medium text-[color:var(--muted-foreground)] backdrop-blur-md transition-colors hover:text-[color:var(--fg-strong)]"
					style="background: var(--glass); border-color: var(--glass-border);"
				>
					<SendHorizontalIcon class="size-3" />
					{prompt}
				</button>
			{/each}
		</div>

		<div class="pt-2">
			<button
				type="button"
				onclick={onContinue}
				class="inline-flex items-center justify-center rounded-full bg-primary px-7 py-2.5 text-[13px] font-medium text-primary-foreground transition-colors hover:bg-[color:var(--primary)]/90"
				style="box-shadow: 0 8px 28px -10px var(--glow), 0 0 0 1px rgba(255,255,255,0.06) inset;"
			>
				Continue
			</button>
		</div>
	{/if}
</div>
